<script setup lang="ts">
import { ref, onMounted, computed, useId, watch } from 'vue'
import { type ErrorType } from '@/types/Errors'

const value = ref()

const emit = defineEmits(['update:modelValue'])

type Numberish = number | string

interface Props {
    errors: ErrorType
    data?: string
    label?: string
    maxLength?: number
    field: string
    format: string
    required?: boolean
    placeholder?: string
    containerClasses?: string
    /** Short hint rendered under the control. */
    hint?: string
    autocomplete?: string
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    format: () => 'text'
})

/** Ties the label to the control, which it previously wasn't. */
const inputId = useId()

const inputVal = computed({
    get: () => {
        return value.value
    },
    set: (val) => {
        value.value = val
        emit('update:modelValue', val)
    }
})

const hasError = computed(
    () => !!props.field && !!props.errors?.[props.field] && props.errors[props.field].length > 0
)

onMounted(() => {
    if (props.data) value.value = props.data
})

/**
 * Keep the control in step with `data` after mount.
 *
 * `data` used to be read on mount only, so the field ignored the prop from then
 * on: a profile that finished loading after this component mounted left the
 * input blank, and a parent resetting its form state (a Discard button) went on
 * showing the edited text. The equality guard makes it idempotent, so a parent
 * binding `v-model` and `:data` to the same value — the common case here —
 * doesn't fight itself while typing.
 */
watch(
    () => props.data,
    (next) => {
        if (next !== undefined && next !== value.value) value.value = next
    }
)

/**
 * Only the date/time pickers get `showPicker`.
 *
 * This used to be a hardcoded `onclick="this.showPicker()"` attribute on every
 * input the app renders — text, email, tel, number and password included — where
 * the call is either a no-op or throws `InvalidStateError` depending on the
 * browser. Calling it on a click is also the only way to open a native picker
 * reliably, so date fields genuinely want it.
 */
const PICKER_FORMATS = ['date', 'datetime-local', 'month', 'time', 'week']

function openPickerIfDate(event: MouseEvent) {
    if (!PICKER_FORMATS.includes(props.format)) return

    const target = event.target as HTMLInputElement & { showPicker?: () => void }
    if (typeof target.showPicker !== 'function') return

    try {
        target.showPicker()
    } catch {
        // Safari and Firefox throw when the input isn't user-activated or the
        // type doesn't support a picker. The field still types fine.
    }
}
</script>
<template>
    <div class="form-group" :class="containerClasses">
        <label v-if="!!label" :for="inputId" class="form-label"
            >{{ label }}<span v-if="required" class="pl-1 text-danger">*</span></label
        >
        <input
            :id="inputId"
            v-model="inputVal"
            class="form-control"
            :maxlength="maxLength as Numberish"
            :type="format"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :disabled="disabled"
            :aria-invalid="hasError ? 'true' : undefined"
            :aria-describedby="hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined"
            :class="hasError ? 'border-danger' : ''"
            @click="openPickerIfDate"
        />
        <i v-if="hasError" :id="`${inputId}-error`" class="form-error">{{ errors[field][0] }}</i>
        <p v-else-if="hint" :id="`${inputId}-hint`" class="text-xs text-fg-subtle">{{ hint }}</p>
    </div>
</template>
<style scoped></style>
